import React, { useCallback } from 'react'
import { styled } from '@mui/material/styles'
import { Cancel, Check } from '@mui/icons-material'
import { BaseModalProps } from './modal'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
} from '@mui/material'
import { Button } from 'react-admin'

const PREFIX = 'RaConfirm'

const classes = {
  content: `${PREFIX}-content`,
  confirmPrimary: `${PREFIX}-confirmPrimary`,
  confirmWarning: `${PREFIX}-confirmWarning`,
  iconPaddingStyle: `${PREFIX}-iconPaddingStyle`,
}

const StyledDialog = styled(Dialog)(({ theme }: any) => ({
  [`& .${classes.content}`]: {
    minWidth: 400,
  },

  [`& .${classes.confirmPrimary}`]: {
    color: theme.palette.primary.main,
  },

  [`& .${classes.confirmWarning}`]: {
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor: alpha(theme.palette.error.main, 0.12),
      // Reset on mouse devices
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },

  [`& .${classes.iconPaddingStyle}`]: {
    paddingRight: '0.5em',
  },
}))

/**
 * Modal
 *
 * @example
 * <Modal
 *     isOpen={true}
 *     title="Delete Item"
 *     confirm="Yes"
 *     confirmColor="primary"
 *     ConfirmIcon=ActionCheck
 *     CancelIcon=AlertError
 *     cancel="Cancel"
 *     onConfirm={() => { // do something }}
 *     onClose={() => { // do something }}
 * />
 */
const Modal: React.FC<BaseModalProps> = (props) => {
  const {
    isOpen = false,
    isWarning = false,
    loading,
    title,
    confirm = 'Confirm',
    cancel = 'Cancel',
    onClose,
    onConfirm,
    children,
  } = props

  const handleConfirm = useCallback(
    (e: any) => {
      e.stopPropagation()
      onConfirm()
    },
    [onConfirm]
  )

  const handleClick = useCallback((e: any) => {
    e.stopPropagation()
  }, [])

  return (
    <StyledDialog
      open={isOpen}
      onClose={onClose}
      onClick={handleClick}
      aria-labelledby="alert-dialog-title"
    >
      <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
      <DialogContent className={classes.content}>{children}</DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={onClose}>
          <>
            <Cancel className={classes.iconPaddingStyle} />
            {cancel}
          </>
        </Button>
        <Button
          disabled={loading}
          onClick={handleConfirm}
          className={
            isWarning ? classes.confirmWarning : classes.confirmPrimary
          }
          autoFocus
        >
          <>
            <Check className={classes.iconPaddingStyle} />
            {confirm}
          </>
        </Button>
      </DialogActions>
    </StyledDialog>
  )
}

export default Modal
